import React, { useState, useEffect } from "react";
import { Button, Text, Box, Heading } from "@blasterjs/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight, faCaretDown } from "@fortawesome/free-solid-svg-icons";
import {
  QueryObjectType,
  QueryStatus,
  CaseWithImages,
  ImageAndQuery,
  QueryDetailRecord,
  QueryDetailComment,
  QueryDetail,
  QueryDetails,
  RoleName,
  QueryResolutionType
} from "../models";
import { useAppSelector, useAppDispatch } from "../hooks";
import { queryDetailsMetadataFetch } from "../slices/queryDetails";
import RespondQueryDialog from "./RespondQueryDialog";
import CloseQueryDialog from "./CloseQueryDialog";
import CancelQueryDialog from "./CancelQueryDialog";
import CommentQueryDialog from "./CommentQueryDialog";
import FollowUpQueryDialog from "./FollowUpQueryDialog";
import ReplaceImage from "./ReplaceImage";
import CreateQueryFlow from "./CreateQueryFlow";
import { userSummariesFetch } from "../slices/users";
import DateYearMonthDay from "./DateYearMonthDay";
import DateYearMonthDayTime from "./DateYearMonthDayTime";
import { LoggedInUser, RolePermissions } from "../permissions";
import styled from "styled-components";

interface QueryListProps {
  readonly histoCase: CaseWithImages | null;
  readonly selectedImage: ImageAndQuery | null;
  readonly queryObjectType: QueryObjectType;
  readonly loggedInUser: LoggedInUser;
  readonly queryDetails: QueryDetails;
}

const OpenQueryButton = styled(Button)`
  color: #231f20;
  border: 1px solid #86888c;
  background-color: #f5f8fa;
  border-radius: 2px;
  width: 100%;
`;

const QueryCountText = styled(Text)`
  background-color: #dde0e6;
  display: flex;
  justify-content: center;
  border-radius: 4px;
  color: #000000;
  padding-top: 2px;
  padding-right: 6px;
  padding-bottom: 2px;
  padding-left: 6px;
`;

const QueryList = ({
  histoCase,
  selectedImage,
  queryObjectType,
  loggedInUser,
  queryDetails
}: QueryListProps) => {
  const dispatch = useAppDispatch();

  const studyId =
    queryObjectType === QueryObjectType.Case && histoCase
      ? histoCase.caseWithStatus.studyId
      : selectedImage && selectedImage.image.studyId
      ? selectedImage.image.studyId
      : null;
  const caseId =
    queryObjectType === QueryObjectType.Case && histoCase
      ? histoCase.caseWithStatus.id
      : selectedImage && selectedImage.image.caseId
      ? selectedImage.image.caseId
      : "";
  const imageId = selectedImage ? selectedImage.image.id : "";

  // Store state variables
  const userSummariesResource = useAppSelector(state => state.users.userSummaries);

  const queryDetailsMetadataResource = useAppSelector(
    state => state.queryDetails.queryDetailsMetadata
  );
  const queryDetailsMetadata =
    "resource" in queryDetailsMetadataResource ? queryDetailsMetadataResource.resource : null;

  const userRoleName = loggedInUser.user.workAsRole?.name || null;
  const userRoleId = loggedInUser.user.workAsRole?.id || null;
  const userId = loggedInUser.user.id;

  const userSummaries =
    "resource" in userSummariesResource ? userSummariesResource.resource.slice() : [];

  const queryCaseReasons = queryDetailsMetadata ? queryDetailsMetadata.caseReasons.slice() : [];
  const queryImageReasons = queryDetailsMetadata ? queryDetailsMetadata.imageReasons.slice() : [];
  const queryRoles = queryDetailsMetadata ? queryDetailsMetadata.roles.slice() : [];

  // Local state variables
  const [selectedQueryTab, setSelectedQueryTab] = useState(queryObjectType);
  const [selectedQueryTypeId, setSelectedQueryTypeId] = useState(
    queryObjectType === QueryObjectType.Case && histoCase && histoCase.caseWithStatus.id
      ? histoCase.caseWithStatus.id
      : selectedImage && imageId
      ? imageId
      : ""
  );
  const [createQuery, setCreateQuery] = useState(false);

  const [closedQueryTabOpen, setClosedQueryTabOpen] = useState(false);
  const [openQueryTabOpen, setOpenQueryTabOpen] = useState(false);
  const [answeredQueryTabOpen, setAnsweredQueryTabOpen] = useState(false);

  const [openResponseDialog, setOpenResponseDialog] = useState(false);
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [openFollowUpDialog, setOpenFollowUpDialog] = useState(false);

  const [updateQuery, setUpdateQuery] = useState<QueryDetailRecord>({} as QueryDetailRecord);
  const [updateQueryComments, setUpdateQueryComments] = useState<Array<QueryDetailComment>>([]);

  const onClosedQueryTabOpenClick = () => {
    setClosedQueryTabOpen(!closedQueryTabOpen);
  };
  const onOpenQueryTabOpenClick = () => {
    setOpenQueryTabOpen(!openQueryTabOpen);
  };
  const onAnsweredQueryTabOpenClick = () => {
    setAnsweredQueryTabOpen(!answeredQueryTabOpen);
  };

  useEffect(() => {
    dispatch(userSummariesFetch());
    dispatch(queryDetailsMetadataFetch(studyId));
  }, [setSelectedQueryTab]);

  const openCaseQueries = queryDetails.filter(queryDetail => {
    const query = queryDetail.query;
    return (
      queryObjectType === QueryObjectType.Case &&
      histoCase &&
      query.caseId === histoCase.caseWithStatus.id &&
      query.objectType === QueryObjectType.Case &&
      query.queryStatus === QueryStatus.Open &&
      query.resolution !== QueryResolutionType.Resolved
    );
  });

  const answeredCaseQueries = queryDetails.filter(queryDetail => {
    const query = queryDetail.query;
    return (
      queryObjectType === QueryObjectType.Case &&
      histoCase &&
      query.caseId === histoCase.caseWithStatus.id &&
      query.objectType === QueryObjectType.Case &&
      query.queryStatus === QueryStatus.Open &&
      query.resolution === QueryResolutionType.Resolved
    );
  });

  const closedCaseQueries = queryDetails.filter(queryDetail => {
    const query = queryDetail.query;
    return (
      queryObjectType === QueryObjectType.Case &&
      histoCase &&
      query.caseId === histoCase.caseWithStatus.id &&
      query.objectType === QueryObjectType.Case &&
      query.queryStatus === QueryStatus.Closed
    );
  });

  const openImageQueries = queryDetails.filter(queryDetail => {
    const query = queryDetail.query;
    return (
      selectedImage &&
      query.objectId === imageId &&
      query.queryStatus === QueryStatus.Open &&
      query.resolution !== QueryResolutionType.Resolved
    );
  });

  const answeredImageQueries = queryDetails.filter(queryDetail => {
    const query = queryDetail.query;
    return (
      selectedImage &&
      query.objectId === imageId &&
      query.queryStatus === QueryStatus.Open &&
      query.resolution === QueryResolutionType.Resolved
    );
  });

  const closedImageQueries = queryDetails.filter(queryDetail => {
    const query = queryDetail.query;
    return selectedImage && query.objectId === imageId && query.queryStatus === QueryStatus.Closed;
  });

  // Helper functions
  const getSelectableRoles = (queryType: QueryObjectType) => {
    if (queryType === QueryObjectType.Case) {
      return queryRoles.filter(
        role => queryCaseReasons.find(reason => role.id === reason.withRoleId) !== undefined
      );
    } else if (queryType === QueryObjectType.Image) {
      return queryRoles.filter(
        role => queryImageReasons.find(reason => role.id === reason.withRoleId) !== undefined
      );
    } else {
      return [];
    }
  };

  const getSelectableReasons = (queryType: QueryObjectType) => {
    if (queryType === QueryObjectType.Case) {
      return queryCaseReasons.slice();
    } else if (queryType === QueryObjectType.Image) {
      return queryImageReasons.slice();
    } else {
      return [];
    }
  };

  const canUserRoleReplaceImage = (userRoleName: RoleName) => {
    return (
      userRoleName === RoleName.StudyAdmin ||
      userRoleName === RoleName.ISC ||
      userRoleName === RoleName.LabTech ||
      userRoleName === RoleName.Uploader
    );
  };

  const canUserCancelQuery = (userRoleName: RoleName) => {
    return (
      userRoleName === RoleName.StudyAdmin ||
      userRoleName === RoleName.ISC ||
      userRoleName === RoleName.SystemAdmin
    );
  };

  // All buttons
  const openCreateQueryButton = () => {
    const onOpenCreateQueryButtonClick = () => {
      setCreateQuery(true);
    };

    return (
      <>
        {((loggedInUser.can([RolePermissions.Q_IssueCaseQuery]) &&
          selectedQueryTab == QueryObjectType.Case) ||
          (loggedInUser.can([RolePermissions.Q_IssueImageQueryStudy]) &&
            selectedQueryTab == QueryObjectType.Image)) && (
          <OpenQueryButton onClick={onOpenCreateQueryButtonClick}>
            Open{" "}
            {selectedQueryTab === QueryObjectType.Image
              ? "Image"
              : selectedQueryTab === QueryObjectType.Case
              ? "Case"
              : null}{" "}
            Query
          </OpenQueryButton>
        )}
      </>
    );
  };

  const respondButton = (queryDetail: QueryDetail) => {
    const onRespondButtonClick = () => {
      setUpdateQuery(queryDetail.query);
      setUpdateQueryComments(queryDetail.comments);
      setOpenResponseDialog(!openResponseDialog);
    };

    return (
      <>
        {loggedInUser.can([RolePermissions.Q_MarkQueryasResolved]) && (
          <Button
            key={queryDetail.query.id + "respond-button"}
            onClick={onRespondButtonClick}
            className="query-blue-button"
            width="100%"
          >
            Mark as Resolved
          </Button>
        )}
      </>
    );
  };

  const commentButton = (queryDetail: QueryDetail) => {
    const onCommentButtonClick = () => {
      setUpdateQuery(queryDetail.query);
      setUpdateQueryComments(queryDetail.comments);
      setOpenCommentDialog(!openCommentDialog);
    };

    return (
      <>
        {loggedInUser.can([RolePermissions.Q_Commentonassignedqueriesonly]) && (
          <Button
            key={queryDetail.query.id + "comment-button"}
            onClick={onCommentButtonClick}
            className="query-white-button"
            width="100%"
          >
            Comment
          </Button>
        )}
      </>
    );
  };

  const followUpButton = (queryDetail: QueryDetail) => {
    const followUpButtonClick = () => {
      setUpdateQuery(queryDetail.query);
      setUpdateQueryComments(queryDetail.comments);
      setOpenFollowUpDialog(!openFollowUpDialog);
    };

    return (
      <>
        {loggedInUser.can([RolePermissions.Q_Flagforfollowup]) && (
          <Button
            key={queryDetail.query.id + "follow-up-button"}
            onClick={followUpButtonClick}
            className="query-white-button"
            width="100%"
          >
            Follow Up
          </Button>
        )}
      </>
    );
  };

  const replaceImageButton = (query: QueryDetailRecord) => {
    if (selectedImage !== null && query.objectType === QueryObjectType.Image) {
      return <ReplaceImage key={query.id + "replace-image-button"} imageAndQuery={selectedImage} />;
    }
  };

  const cancelButton = (queryDetail: QueryDetail) => {
    const onCancelButtonClick = () => {
      setUpdateQuery(queryDetail.query);
      setUpdateQueryComments(queryDetail.comments);
      setOpenCancelDialog(!openCancelDialog);
    };

    return (
      <>
        <Button
          key={queryDetail.query.id + "cancel-button"}
          onClick={onCancelButtonClick}
          className="query-white-button"
          width="100%"
        >
          Cancel
        </Button>
      </>
    );
  };

  const closeQueryButton = (queryDetail: QueryDetail) => {
    const onCloseQueryButtonClick = () => {
      setUpdateQuery(queryDetail.query);
      setUpdateQueryComments(queryDetail.comments);
      setOpenCloseDialog(!openCloseDialog);
    };

    return (
      <>
        {loggedInUser.can([
          RolePermissions.Q_Close_CancelQueryunderUserRoleOnly,
          RolePermissions.Q_Close_CancelQueryunderAssignedStudies
        ]) && (
          <Button
            key={queryDetail.query.id + "close-button"}
            onClick={onCloseQueryButtonClick}
            className="query-blue-button"
            width="100%"
          >
            Close Query
          </Button>
        )}
      </>
    );
  };

  // Button groupings
  const issuerAndRecipientOpenQueryButtons = (queryDetail: QueryDetail) => {
    const canReplaceImage = userRoleName ? canUserRoleReplaceImage(userRoleName) : false;

    const buttons = (
      <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1} width="100%">
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box width="49%">{respondButton(queryDetail)}</Box>
          <Box width="49%">{cancelButton(queryDetail)}</Box>
        </Box>
        {canReplaceImage && queryDetail.query.objectType === QueryObjectType.Image ? (
          <Box mt={1} width="100%">
            {replaceImageButton(queryDetail.query)}
          </Box>
        ) : null}
        <Box display="flex" justifyContent="space-between" mt={1} width="100%">
          <Box width="49%">{followUpButton(queryDetail)}</Box>
          <Box width="49%">{commentButton(queryDetail)}</Box>
        </Box>
      </Box>
    );

    return buttons;
  };

  const issuerOpenQueryButtons = (queryDetail: QueryDetail) => {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1} width="100%">
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box width="49%">{followUpButton(queryDetail)}</Box>
          <Box width="49%">{cancelButton(queryDetail)}</Box>
        </Box>
        <Box mt={1} width="100%">
          {commentButton(queryDetail)}
        </Box>
      </Box>
    );
  };

  const recipientOpenQueryButtons = (queryDetail: QueryDetail) => {
    const canReplaceImage = userRoleName ? canUserRoleReplaceImage(userRoleName) : false;
    const canCancelQuery = userRoleName ? canUserCancelQuery(userRoleName) : false;

    const buttons = (
      <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1} width="100%">
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box width="49%">{respondButton(queryDetail)}</Box>
          <Box width="49%">{commentButton(queryDetail)}</Box>
        </Box>
        {canCancelQuery ? (
          <Box mt={1} width="100%">
            {cancelButton(queryDetail)}
          </Box>
        ) : null}
        {canReplaceImage && queryDetail.query.objectType === QueryObjectType.Image ? (
          <Box mt={1} width="100%">
            {replaceImageButton(queryDetail.query)}
          </Box>
        ) : null}
      </Box>
    );

    return buttons;
  };

  const notIssuerOrRecepientOpenQueryButtons = (queryDetail: QueryDetail) => {
    const canCancelQuery = userRoleName ? canUserCancelQuery(userRoleName) : false;

    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1} width="100%">
        <Box mt={1} width="100%">
          {commentButton(queryDetail)}
        </Box>
        {canCancelQuery ? (
          <Box mt={1} width="100%">
            {cancelButton(queryDetail)}
          </Box>
        ) : null}
      </Box>
    );
  };

  const issuerAnsweredQueriesButtons = (queryDetail: QueryDetail) => {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1} width="100%">
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box width="49%">{closeQueryButton(queryDetail)}</Box>
          <Box width="49%">{commentButton(queryDetail)}</Box>
        </Box>
      </Box>
    );
  };

  const imageCaseSelectionTab = () => {
    const onImageCaseSelectionTabClick = (type: QueryObjectType) => {
      setOpenQueryTabOpen(false);
      setClosedQueryTabOpen(false);
      setAnsweredQueryTabOpen(false);

      setSelectedQueryTab(type);
      setSelectedQueryTypeId(
        type === QueryObjectType.Case && histoCase && histoCase.caseWithStatus.id
          ? histoCase.caseWithStatus.id
          : selectedImage && imageId
          ? imageId
          : ""
      );
    };

    return (
      <>
        <div style={{ display: "flex" }}>
          <div
            className={
              selectedQueryTab === QueryObjectType.Image
                ? "image-case-button-clicked"
                : "image-case-button-unclicked"
            }
            onClick={() => onImageCaseSelectionTabClick(QueryObjectType.Image)}
          >
            Image&nbsp;
            <QueryCountText>{openImageQueries.length + answeredImageQueries.length}</QueryCountText>
          </div>
          <div
            className={
              selectedQueryTab === QueryObjectType.Case
                ? "image-case-button-clicked"
                : "image-case-button-unclicked"
            }
            onClick={() => onImageCaseSelectionTabClick(QueryObjectType.Case)}
          >
            Case&nbsp;
            <QueryCountText>{openCaseQueries.length + answeredCaseQueries.length}</QueryCountText>
          </div>
        </div>
        <br />
      </>
    );
  };

  const createCommentList = (comments: Array<QueryDetailComment>) => {
    const commentsList = comments.map(comment => {
      return (
        <div key={comment.id}>
          <Text style={{ fontWeight: 400, fontSize: 12, color: "#111111" }}>
            {comment.firstName}&nbsp;{comment.lastName}&nbsp;-&nbsp;
            <DateYearMonthDayTime date={comment.createdAt} />
          </Text>
          <br />
          <Text className={"query-list-result"}>{comment.commentText}</Text>
          <br />
          <br />
        </div>
      );
    });

    return commentsList;
  };

  const createQueryList = (queryDetails: QueryDetails) => {
    const queryDetailsList = queryDetails.map(queryDetail => {
      const query = queryDetail.query;
      const comments = queryDetail.comments;

      const queryWith = queryDetailsMetadata
        ? queryDetailsMetadata.roles.find(role => role.id === query.withRoleId)
        : null;
      const queryWithText = queryWith ? queryWith.name : "";

      const isRecipient = query.withRoleId === userRoleId;
      const isIssuer = query.openedByRoleId === userRoleId;
      const isOpenQuery =
        query.queryStatus === QueryStatus.Open && query.resolution !== QueryResolutionType.Resolved;
      const isAnsweredQuery =
        query.queryStatus === QueryStatus.Open && query.resolution === QueryResolutionType.Resolved;
      const isClosedQuery = query.queryStatus === QueryStatus.Closed;

      const getButtonGrouping = () => {
        if (isOpenQuery) {
          if (isIssuer && isRecipient) {
            return issuerAndRecipientOpenQueryButtons(queryDetail);
          } else if (isIssuer) {
            return issuerOpenQueryButtons(queryDetail);
          } else if (isRecipient) {
            return recipientOpenQueryButtons(queryDetail);
          } else {
            return notIssuerOrRecepientOpenQueryButtons(queryDetail);
          }
        } else if (isAnsweredQuery) {
          if (isIssuer || (userRoleName ? canUserCancelQuery(userRoleName) : false)) {
            return issuerAnsweredQueriesButtons(queryDetail);
          }
        }

        return null;
      };

      return (
        <Box
          key={query.id}
          style={{ marginBottom: "20px", backgroundColor: "#F9F9FA" }}
          width="100%"
        >
          <hr />
          {query.objectType === QueryObjectType.Image ? (
            <>
              <Box display="flex" width="100%">
                <Text textAlign="left" className={"query-list-header"}>
                  File Name
                </Text>
                <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
                  {selectedImage ? selectedImage.image.name : ""}
                </Text>
              </Box>
              <hr />
            </>
          ) : null}
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-header"}>
              Query Reason
            </Text>
            <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
              {query.reasonText} {query.childReasonText ? " - " + query.childReasonText : ""}
            </Text>
          </Box>
          <hr />
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-header"}>
              Query With
            </Text>
            <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
              {queryWithText}
            </Text>
          </Box>
          <hr />
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-header"}>
              Follow Up
            </Text>
            <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
              <DateYearMonthDay date={query.latestFollowUpOnDt} />
            </Text>
          </Box>
          <hr />
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-header"}>
              Opened Date
            </Text>
            <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
              <DateYearMonthDay date={query.openedAt} />
            </Text>
          </Box>
          <hr />
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-header"}>
              Issued By
            </Text>
            <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
              {userSummaries.find(user => user.username === query.createdBy)?.firstName}&nbsp;
              {userSummaries.find(user => user.username === query.createdBy)?.lastName}
            </Text>
          </Box>
          {(query.queryStatus === QueryStatus.Open &&
            query.resolution === QueryResolutionType.Resolved) ||
          query.queryStatus === QueryStatus.Closed ? (
            <>
              <hr />
              <Box display="flex" width="100%">
                <Text textAlign="left" className={"query-list-header"}>
                  Response
                </Text>
                <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
                  {query.resolutionOption}
                </Text>
              </Box>
              <hr />
              <Box display="flex" width="100%">
                <Text textAlign="left" className={"query-list-header"}>
                  Response Details
                </Text>
                <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
                  {query.resolutionText}
                </Text>
              </Box>
              <hr />
              <Box display="flex" width="100%">
                <Text textAlign="left" className={"query-list-header"}>
                  Response by
                </Text>
                <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
                  {userSummaries.find(user => user.id === query.resolvedBy)?.firstName}&nbsp;
                  {userSummaries.find(user => user.id === query.resolvedBy)?.lastName}
                </Text>
              </Box>
            </>
          ) : null}
          {isClosedQuery ? (
            <>
              <hr />
              <Box display="flex" width="100%">
                <Text textAlign="left" className={"query-list-header"}>
                  Close Date
                </Text>
                <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
                  <DateYearMonthDay date={query.closedAt} />
                </Text>
              </Box>
              <hr />
              <Box display="flex" width="100%">
                <Text textAlign="left" className={"query-list-header"}>
                  Closed By
                </Text>
                <Text ml={"auto"} textAlign="right" className={"query-list-result"}>
                  {userSummaries.find(user => user.id === query.closedBy)?.firstName}&nbsp;
                  {userSummaries.find(user => user.id === query.closedBy)?.lastName}
                </Text>
              </Box>
            </>
          ) : null}
          <hr />
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-header"}>
              Comments
            </Text>
          </Box>
          <Box display="flex" width="100%">
            <Text textAlign="left" className={"query-list-result"} ml={1}>
              {createCommentList(comments)}
            </Text>
          </Box>

          <Box></Box>
          {getButtonGrouping()}
        </Box>
      );
    });

    return (
      <Box style={{ width: "100%", borderRadius: "8px", marginTop: "5px" }}>{queryDetailsList}</Box>
    );
  };

  const createQueriesContainer = (
    openQueries: QueryDetails,
    answeredQueries: QueryDetails,
    closedQueries: QueryDetails
  ) => {
    return (
      <>
        <Box style={{ display: "flex", alignItems: "center" }} mb={1}>
          <Box>
            <button
              style={{ border: "none", backgroundColor: "inherit", cursor: "pointer" }}
              onClick={onOpenQueryTabOpenClick}
            >
              {openQueryTabOpen ? (
                <FontAwesomeIcon icon={faCaretDown} />
              ) : (
                <FontAwesomeIcon icon={faCaretRight} />
              )}
            </button>
          </Box>
          <Heading
            as="h6"
            ml="1"
            mr="1"
            style={{ color: "#111111", fontWeight: "700", fontSize: "16px" }}
          >
            Open Queries
          </Heading>
          <QueryCountText>{openQueries.length}</QueryCountText>
        </Box>

        {openQueries.length > 0 && openQueryTabOpen ? createQueryList(openQueries) : null}

        <Box style={{ display: "flex", alignItems: "center" }} mb={1}>
          <Box>
            <button
              style={{ border: "none", backgroundColor: "inherit", cursor: "pointer" }}
              onClick={onAnsweredQueryTabOpenClick}
            >
              {answeredQueryTabOpen ? (
                <FontAwesomeIcon icon={faCaretDown} />
              ) : (
                <FontAwesomeIcon icon={faCaretRight} />
              )}
            </button>
          </Box>
          <Heading
            as="h6"
            ml="1"
            mr="1"
            style={{ color: "#111111", fontWeight: "700", fontSize: "16px" }}
          >
            Answered Queries
          </Heading>
          <QueryCountText>{answeredQueries.length}</QueryCountText>
        </Box>

        {answeredQueries.length > 0 && answeredQueryTabOpen
          ? createQueryList(answeredQueries)
          : null}

        <Box style={{ display: "flex", alignItems: "center" }}>
          <Box>
            <button
              style={{ border: "none", backgroundColor: "inherit", cursor: "pointer" }}
              onClick={onClosedQueryTabOpenClick}
            >
              {closedQueryTabOpen ? (
                <FontAwesomeIcon icon={faCaretDown} />
              ) : (
                <FontAwesomeIcon icon={faCaretRight} />
              )}
            </button>
          </Box>
          <Heading
            as="h6"
            ml="1"
            mr="1"
            style={{ color: "#111111", fontWeight: "700", fontSize: "16px" }}
          >
            Closed Queries
          </Heading>
          <QueryCountText>{closedQueries.length}</QueryCountText>
        </Box>

        {closedQueries.length > 0 && closedQueryTabOpen ? createQueryList(closedQueries) : null}
      </>
    );
  };

  if (!createQuery) {
    return (
      <>
        <Box mt={2} style={{ backgroundColor: "#FFFFFF" }}>
          {openCreateQueryButton()}

          <br />
          <br />

          {queryObjectType === QueryObjectType.Case ? imageCaseSelectionTab() : null}

          {queryObjectType === QueryObjectType.Case
            ? selectedQueryTab === QueryObjectType.Case
              ? createQueriesContainer(openCaseQueries, answeredCaseQueries, closedCaseQueries)
              : createQueriesContainer(openImageQueries, answeredImageQueries, closedImageQueries)
            : queryObjectType === QueryObjectType.Image
            ? createQueriesContainer(openImageQueries, answeredImageQueries, closedImageQueries)
            : null}
        </Box>

        <RespondQueryDialog
          key={"respond-dialog"}
          openDialog={openResponseDialog}
          setOpenDialog={setOpenResponseDialog}
          queryId={updateQuery.id}
        />
        <CommentQueryDialog
          key={"comment-dialog"}
          openDialog={openCommentDialog}
          setOpenDialog={setOpenCommentDialog}
          query={updateQuery}
          issuer={updateQuery.openedBy === userId}
        />
        <FollowUpQueryDialog
          key={"follow-up-dialog"}
          openDialog={openFollowUpDialog}
          setOpenDialog={setOpenFollowUpDialog}
          query={updateQuery}
        />
        <CancelQueryDialog
          key={"cancel-dialog"}
          openDialog={openCancelDialog}
          setOpenDialog={setOpenCancelDialog}
          query={updateQuery}
          comments={updateQueryComments}
          imageName={
            selectedQueryTab === QueryObjectType.Case ? undefined : selectedImage?.image.name
          }
        />
        <CloseQueryDialog
          key={"close-dialog"}
          openDialog={openCloseDialog}
          setOpenDialog={setOpenCloseDialog}
          query={updateQuery}
          comments={updateQueryComments}
          imageName={
            selectedQueryTab === QueryObjectType.Case ? undefined : selectedImage?.image.name
          }
        />
      </>
    );
  } else {
    return (
      <Box>
        <CreateQueryFlow
          loggedInUser={loggedInUser}
          createQuery={createQuery}
          setCreateQuery={setCreateQuery}
          reasons={getSelectableReasons(selectedQueryTab)}
          roles={getSelectableRoles(selectedQueryTab)}
          queryType={selectedQueryTab}
          queryTypeId={selectedQueryTypeId}
          studyId={studyId}
          caseId={caseId}
        />
      </Box>
    );
  }
};

export default QueryList;
